/**
 * CreditNotesListPage — clinic-wide roll-up of every issued CN.
 *
 * Filters: patient search, invoice search, status, date window.
 * Row action: opens the CN detail page (printable).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API, fmtINR, fmtDate } from './billingUtils';

const REASON_LABELS = {
  data_error:         'Data error',
  price_adjustment:   'Price adj.',
  post_sale_discount: 'Post-sale disc.',
  return:             'Return',
  service_deficiency: 'Deficiency',
  duplicate_billing:  'Duplicate',
  other:              'Other',
};

export default function CreditNotesListPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (status) params.status = status;
        const r = await axios.get(`${API}/billing/credit-notes`, { params });
        if (alive) setRows(r.data || []);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [status]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.trim().toLowerCase();
    return rows.filter((r) =>
      (r.cn_no || '').toLowerCase().includes(s)
      || (r.invoice_no || '').toLowerCase().includes(s)
      || (r.patient_name || '').toLowerCase().includes(s)
    );
  }, [rows, q]);

  return (
    <div className="p-4 space-y-3" data-testid="credit-notes-list-page">
      <div className="bg-white rounded-lg border border-slate-200 p-3 flex flex-wrap items-center gap-2">
        <input
          data-testid="cn-list-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search CN no / invoice no / patient name"
          className="border border-slate-300 rounded px-2 py-1 text-xs flex-1 min-w-[220px]" />
        <select
          data-testid="cn-list-status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="border border-slate-300 rounded px-2 py-1 text-xs bg-white font-semibold">
          <option value="">All statuses</option>
          <option value="issued">Issued</option>
          <option value="voided">Voided</option>
        </select>
        <div className="ml-auto text-[11px] text-slate-500">
          {filtered.length} credit note{filtered.length === 1 ? '' : 's'}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr className="text-left">
              <th className="px-3 py-2 font-semibold">CN No</th>
              <th className="px-3 py-2 font-semibold">Issued</th>
              <th className="px-3 py-2 font-semibold">Against Invoice</th>
              <th className="px-3 py-2 font-semibold">Patient</th>
              <th className="px-3 py-2 font-semibold">Reason</th>
              <th className="px-3 py-2 font-semibold">Settlement</th>
              <th className="px-3 py-2 font-semibold text-right">Amount</th>
              <th className="px-3 py-2 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400"
                     data-testid="cn-list-empty">
                No credit notes yet.
              </td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.cn_id} className="border-t border-slate-100 hover:bg-slate-50"
                  data-testid={`cn-row-${r.cn_id}`}>
                <td className="px-3 py-2 font-mono">
                  <Link to={`/billing/credit-note/${r.cn_id}`}
                        className="text-emerald-700 hover:underline">
                    {r.cn_no}
                  </Link>
                </td>
                <td className="px-3 py-2 text-slate-600">{fmtDate(r.issued_at)}</td>
                <td className="px-3 py-2 font-mono">
                  <Link to={`/billing/invoice/${r.invoice_id}`}
                        className="text-slate-700 hover:text-emerald-700 hover:underline">
                    {r.invoice_no}
                  </Link>
                </td>
                <td className="px-3 py-2 text-slate-700">{r.patient_name}</td>
                <td className="px-3 py-2 text-slate-600">
                  {REASON_LABELS[r.reason_code] || r.reason_code}
                </td>
                <td className="px-3 py-2 text-slate-600 capitalize">{r.settlement_mode}</td>
                <td className="px-3 py-2 text-right font-semibold text-rose-600 tabular-nums">
                  {fmtINR(r.rounded_total)}
                </td>
                <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                    r.status === 'voided'
                      ? 'bg-slate-200 text-slate-500'
                      : 'bg-rose-100 text-rose-700'
                  }`}>
                    {r.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
